import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme.jsx';
import { apiClient } from '../services/http.js';
import { fetchPlanCatalog } from '../services/planCatalog.js';

const NAV_ANCHORS = [
  { id: 'recursos', label: 'Recursos' },
  { id: 'como-funciona', label: 'Como funciona' },
  { id: 'fontes', label: 'Fontes' },
  { id: 'planos', label: 'Planos' },
  { id: 'seguranca', label: 'Segurança' },
];

const FAQS = [
  {
    question: 'O CatholicGPT substitui a orientação de um padre?',
    answer: 'Não. É uma ferramenta de estudo e formação baseada no Catecismo, na Bíblia e no Magistério — útil para aprofundar o conhecimento da fé, mas confissão, direção espiritual e questões de consciência devem sempre ser tratadas com um sacerdote.',
  },
  {
    question: 'O CatholicGPT é um projeto oficial da Igreja?',
    answer: 'Não. Somos um projeto independente de formação católica com inteligência artificial, sem vínculo institucional oficial com a Igreja Católica, dioceses ou paróquias.',
  },
  {
    question: 'A IA pode errar?',
    answer: 'Sim — como qualquer sistema de IA, respostas podem conter imprecisões. Sempre que uma passagem, citação ou ensinamento for essencial para você, verifique na fonte original (Bíblia, Catecismo, documento do Magistério) ou consulte um sacerdote.',
  },
  {
    question: 'Preciso pagar para experimentar?',
    answer: 'Não. Toda conta nova começa com um período de teste gratuito, com créditos para conversar e explorar os planos de leitura, antes de decidir por uma assinatura.',
  },
];


/* ─── Contact Modal ─── */
const ContactModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const { res } = await apiClient.request('/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  const field = (key, placeholder, type = 'text', required = true) => (
    <div>
      <input
        type={type}
        placeholder={placeholder}
        required={required}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className="w-full bg-surface-container border-0 border-b border-outline-variant/40 px-3 py-2.5 text-on-surface placeholder:text-on-surface-variant/50 outline-none focus:border-primary transition-colors text-sm"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="relative bg-surface-container-low border border-outline-variant/20 rounded-2xl p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-on-surface-variant hover:text-primary transition-colors">
          <span className="material-symbols-outlined">close</span>
        </button>
        <h2 className="font-serif text-2xl text-primary mb-2">Fale Conosco</h2>
        <p className="text-on-surface-variant text-sm mb-6">Envie sua mensagem. Respondemos em até 48h.</p>

        {status === 'success' ? (
          <div className="text-center py-8">
            <span className="material-symbols-outlined text-primary text-5xl mb-4 block">check_circle</span>
            <p className="text-on-surface font-medium">Mensagem enviada!</p>
            <p className="text-on-surface-variant text-sm mt-1">Entraremos em contato em breve.</p>
            <button onClick={onClose} className="mt-6 bg-primary text-on-primary px-6 py-2 rounded-full text-sm font-medium hover:opacity-90 transition-opacity">
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {field('name', 'Seu nome')}
            {field('email', 'Seu e-mail', 'email')}
            {field('phone', 'Telefone (opcional)', 'tel', false)}
            <div>
              <textarea
                placeholder="Sua mensagem..."
                required
                rows={4}
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                className="w-full bg-surface-container border-0 border-b border-outline-variant/40 px-3 py-2.5 text-on-surface placeholder:text-on-surface-variant/50 outline-none focus:border-primary transition-colors text-sm resize-none"
              />
            </div>
            {status === 'error' && (
              <p className="text-error text-sm text-center">Erro ao enviar. Tente novamente.</p>
            )}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full bg-primary text-on-primary font-medium py-3 rounded-full hover:opacity-90 transition-opacity disabled:opacity-60 mt-2"
            >
              {status === 'loading' ? 'Enviando...' : 'Enviar Mensagem'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ─── Theme Toggle Button ─── */
const ThemeToggle = ({ className = '' }) => {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
      className={`p-2 rounded-full text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-all duration-200 ${className}`}
    >
      <span className="material-symbols-outlined text-[20px]">
        {theme === 'dark' ? 'light_mode' : 'dark_mode'}
      </span>
    </button>
  );
};

/* ─── Prévia de Planos (catálogo real do backend, P23A/P23C) ─── */
const LandingPlansPreview = ({ navigate, isLight }) => {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetchPlanCatalog().then(setPlans);
  }, []);

  const paidPlans = plans.filter((p) => !p.is_trial);

  return (
    <div id="planos" className="max-w-6xl w-full pb-24 px-6 scroll-mt-24">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-on-surface mb-4">Planos para cada etapa da sua jornada</h2>
        <p className="text-on-surface-variant font-light leading-relaxed">
          Comece com o período de teste gratuito e evolua quando quiser mais créditos e recursos.
        </p>
      </div>

      {paidPlans.length === 0 ? (
        <p className="text-center text-on-surface-variant/60 text-sm">Carregando planos...</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {paidPlans.map((plan) => {
            const monthlyPrice = (plan.prices?.quarterly ?? 0) / 100 / 3;
            return (
              <div
                key={plan.plan_id}
                className={`rounded-2xl p-6 border ${plan.plan_id === 'regular'
                  ? 'border-primary bg-surface-container-low shadow-[0_12px_40px_rgba(212,175,55,0.15)]'
                  : 'border-outline-variant/30 bg-surface-container-lowest/50'
                  }`}
              >
                <h3 className="font-serif text-lg font-bold text-on-surface mb-1">{plan.name}</h3>
                <p className="text-xs text-on-surface-variant/70 mb-4">{plan.tagline}</p>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-xs font-semibold text-on-surface-variant">R$</span>
                  <span className="text-3xl font-bold text-primary">{monthlyPrice.toFixed(2).replace('.', ',')}</span>
                  <span className="text-xs text-on-surface-variant">/ mês</span>
                </div>
                <p className="text-xs text-on-surface-variant">{plan.credits_per_week} mensagens por semana</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-center mt-8">
        <button
          onClick={() => navigate('/planos')}
          className={`px-8 py-3 rounded-full font-semibold text-sm border transition-all ${isLight
            ? 'bg-[#F2F2F2] border-[#BFBFBF] text-on-surface hover:bg-[#E5E5E5]'
            : 'bg-transparent border-primary text-primary hover:bg-primary hover:text-on-primary'
            }`}
        >
          Ver todos os planos e comparativo completo
        </button>
      </div>
    </div>
  );
};

/* ─── Landing Page ─── */
export const LandingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [modal, setModal] = useState(null);
  const isLight = theme === 'light';

  return (
    <div className="min-h-screen flex flex-col bg-background text-on-surface font-sans">
      {/* TopNavBar */}
      <nav className="fixed top-0 w-full z-50 flex justify-between items-center px-4 md:px-12 py-3 sm:py-4 bg-background/90 backdrop-blur-xl">
        <div className="flex items-center space-x-2 sm:space-x-3 shrink-0">
          <img alt="CatholicGPT Logo" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white p-1" src="/logo-192.png" />
          <div className="text-[1.1rem] sm:text-xl md:text-2xl font-serif tracking-tighter text-primary">CatholicGPT</div>
        </div>

        <div className="hidden lg:flex items-center gap-6 text-sm font-medium text-on-surface-variant">
          {NAV_ANCHORS.map((item) => (
            <a key={item.id} href={`#${item.id}`} className="hover:text-primary transition-colors">
              {item.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
          <button
            onClick={() => navigate('/planos')}
            className="lg:hidden text-[0.85rem] sm:text-sm font-semibold text-on-surface-variant hover:text-primary transition-colors sm:mr-2 px-2 sm:px-0"
          >
            Planos
          </button>
          <ThemeToggle className="scale-90 sm:scale-100" />
          <button
            onClick={() => navigate('/register')}
            className={`hidden md:block border px-6 py-2 rounded-full font-semibold transition-all text-sm ${isLight
              ? 'bg-[#F2F2F2] border-[#BFBFBF] text-on-surface hover:bg-[#E5E5E5]'
              : 'bg-transparent border-primary text-primary hover:bg-primary hover:text-on-primary'
              }`}
          >
            Cadastrar
          </button>
          <button
            onClick={() => navigate('/login')}
            className="bg-primary text-on-primary px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-semibold hover:opacity-90 active:scale-95 transition-all text-[0.85rem] sm:text-sm ml-1 sm:ml-0"
          >
            Entrar
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="flex-grow flex flex-col items-center">
        <section className="relative w-full min-h-[85vh] flex flex-col items-center justify-center px-6 text-center overflow-hidden">
          <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover">
            <source src="/Video_LandingPage.mp4" type="video/mp4" />
          </video>
          {/* overlay adapts to theme */}
          <div className={`absolute inset-0 ${isLight
            ? 'bg-gradient-to-b from-white/60 via-white/50 to-white/70'
            : 'bg-gradient-to-b from-[rgba(5,7,10,0.7)] to-[rgba(5,7,10,0.9)]'
            }`} />

          <div className="relative z-10 max-w-4xl w-full space-y-8">
            <div className={`inline-flex items-center justify-center space-x-2 mb-4 px-4 py-1.5 rounded-full backdrop-blur-sm ${isLight
              ? 'bg-[#F2F2F2]/30'
              : 'border border-primary bg-primary/5'
              }`}>
              <span className="material-symbols-outlined text-primary text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>auto_awesome</span>
              <span className="text-[10px] tracking-[0.3em] uppercase font-sans font-bold text-primary">Veritas et Sapientia</span>
            </div>
            <h1 className={`font-serif text-4xl md:text-7xl font-bold tracking-tight leading-[1.1] ${isLight ? 'text-on-surface' : 'text-white'}`}>
              A Inteligência Artificial a serviço da <span className="text-primary italic">evangelização</span> e da Fé
            </h1>

            <p className={`text-lg md:text-xl max-w-2xl mx-auto font-sans font-light leading-relaxed ${isLight ? 'text-on-surface-variant' : 'text-white/80'}`}>
              Explore o Catecismo, a Bíblia e a Tradição com clareza e profundidade, unindo tecnologia e espiritualidade milenar.
            </p>

            <div className="pt-8 flex flex-col md:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate('/register')}
                className="w-full md:w-auto bg-primary text-on-primary px-10 py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all shadow-lg"
              >
                Começar Jornada
              </button>
              <button
                onClick={() => navigate('/login')}
                className={`w-full md:w-auto px-10 py-4 rounded-xl font-bold text-lg transition-all border ${isLight
                  ? 'bg-[#F2F2F2] border-[#BFBFBF] text-on-surface hover:bg-[#E5E5E5]'
                  : 'bg-transparent border-primary text-primary hover:bg-primary/10 backdrop-blur-md'
                  }`}
              >
                Já tenho conta
              </button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <div id="recursos" className="max-w-3xl w-full pt-24 px-6 text-center scroll-mt-24">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-on-surface mb-4">
            Tudo que você precisa para aprofundar sua fé
          </h2>
          <p className="text-on-surface-variant font-light leading-relaxed">
            Conteúdo fiel ao Magistério e ferramentas para uma jornada diária de formação — não é só um chat, é um ecossistema completo.
          </p>
        </div>

        {/* Muito além de um chatbot */}
        <div className="max-w-4xl w-full pt-12 px-6">
          <div className="bg-surface-container-low border border-outline-variant/20 rounded-2xl p-6 md:p-8">
            <h3 className="font-serif text-xl md:text-2xl font-bold text-on-surface mb-4 text-center">
              Muito além de um chatbot
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {[
                'Um chatbot genérico responde no vácuo; o CatholicGPT cita a Bíblia, o Catecismo e o Magistério como fontes.',
                'Um chatbot esquece a conversa de ontem; aqui você tem plano de leitura, trilhas e caderno espiritual que acompanham sua jornada.',
                'Um chatbot fala com uma única "voz"; aqui você escolhe entre personas (Padre Amigo, Teólogo, Catequista) ou ativa o Sínodo Virtual para ouvir duas ao mesmo tempo.',
                'Um chatbot não assume limites; nós dizemos claramente quando o assunto exige um sacerdote, não uma IA.',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5 text-on-surface-variant">
                  <span className="material-symbols-outlined text-primary text-base shrink-0 mt-0.5">verified</span>
                  <span className="font-light leading-relaxed">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-6xl w-full pt-16 pb-24 grid grid-cols-1 md:grid-cols-3 gap-8 px-6">
          {/* Card: Escrituras */}
          <div className={`group bg-surface-container-low p-10 rounded-2xl border ${isLight
            ? 'border-outline-variant/30 hover:border-outline-variant'
            : 'border-primary'
            } transition-all duration-500 hover:-translate-y-1`}>
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-primary text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>menu_book</span>
            </div>
            <h3 className="font-serif text-2xl mb-3 text-on-surface font-semibold">Escrituras Sagradas</h3>
            <p className="text-on-surface-variant font-light leading-relaxed opacity-90 text-sm mb-4">
              Acesso integral à Bíblia Sagrada — todos os 73 livros do Cânon Católico — com interpretações embasadas no Magistério, nos Padres da Igreja e na Tradição Apostólica.
            </p>
            <ul className="space-y-1 text-xs text-on-surface-variant/70">
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Comentários exegéticos fundamentados</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Liturgia do dia e leituras</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Salmos e orações contextualizadas</li>
            </ul>
          </div>

          {/* Card: Tradição */}
          <div className={`group bg-surface-container-low p-10 rounded-2xl border ${isLight
            ? 'border-outline-variant/30 hover:border-outline-variant'
            : 'border-primary'
            } transition-all duration-500 hover:-translate-y-1`}>
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-primary text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>history_edu</span>
            </div>
            <h3 className="font-serif text-2xl mb-3 text-on-surface font-semibold">Tradição Viva</h3>
            <p className="text-on-surface-variant font-light leading-relaxed opacity-90 text-sm mb-4">
              Mergulhe nos escritos dos Santos Padres, Doutores da Igreja e Concílios Ecumênicos. Do Papa São Leão Magno a São Tomás de Aquino, a sabedoria perene da Igreja ao alcance de todos.
            </p>
            <ul className="space-y-1 text-xs text-on-surface-variant/70">
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Santos Padres e Doutores</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Encíclicas e documentos papais</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Hagiografia e vidas dos santos</li>
            </ul>
          </div>

          {/* Card: Catecismo */}
          <div className={`group bg-surface-container-low p-10 rounded-2xl border ${isLight
            ? 'border-outline-variant/30 hover:border-outline-variant'
            : 'border-primary'
            } transition-all duration-500 hover:-translate-y-1`}>
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-primary text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>church</span>
            </div>
            <h3 className="font-serif text-2xl mb-3 text-on-surface font-semibold">Catecismo e Doutrina</h3>
            <p className="text-on-surface-variant font-light leading-relaxed opacity-90 text-sm mb-4">
              Respostas claras, honestas e fundamentadas sobre a doutrina católica para os desafios morais, éticos e espirituais do mundo contemporâneo. Sem relativismo, sem desvios.
            </p>
            <ul className="space-y-1 text-xs text-on-surface-variant/70">
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Catecismo da Igreja Católica</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Sacramentos e moral cristã</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Apologética e ecumenismo</li>
            </ul>
          </div>

          {/* Card: Leitura Assistida */}
          <div className={`group bg-surface-container-low p-10 rounded-2xl border ${isLight
            ? 'border-outline-variant/30 hover:border-outline-variant'
            : 'border-primary'
            } transition-all duration-500 hover:-translate-y-1`}>
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-primary text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>auto_stories</span>
            </div>
            <h3 className="font-serif text-2xl mb-3 text-on-surface font-semibold">Leitura Assistida</h3>
            <p className="text-on-surface-variant font-light leading-relaxed opacity-90 text-sm mb-4">
              Planos de leitura guiados dia a dia, com lembretes e aprofundamento por Inteligência Artificial sempre que quiser entender mais sobre o texto.
            </p>
            <ul className="space-y-1 text-xs text-on-surface-variant/70">
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Planos temáticos e por livro</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Aprofundamento com IA sob demanda</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Lembretes de leitura diária</li>
            </ul>
          </div>

          {/* Card: Conversas com Personas de IA */}
          <div className={`group bg-surface-container-low p-10 rounded-2xl border ${isLight
            ? 'border-outline-variant/30 hover:border-outline-variant'
            : 'border-primary'
            } transition-all duration-500 hover:-translate-y-1`}>
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-primary text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>forum</span>
            </div>
            <h3 className="font-serif text-2xl mb-3 text-on-surface font-semibold">Conversas com Personas de IA</h3>
            <p className="text-on-surface-variant font-light leading-relaxed opacity-90 text-sm mb-4">
              Converse com Padre Amigo, Teólogo, Catequista ou Diretor Espiritual — cada persona traz um tom e uma profundidade diferentes para suas perguntas.
            </p>
            <ul className="space-y-1 text-xs text-on-surface-variant/70">
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>4 personas especializadas</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Sínodo Virtual: duas personas, uma pergunta</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Histórico de conversas salvo</li>
            </ul>
          </div>

          {/* Card: Trilhas e Caderno Espiritual */}
          <div className={`group bg-surface-container-low p-10 rounded-2xl border ${isLight
            ? 'border-outline-variant/30 hover:border-outline-variant'
            : 'border-primary'
            } transition-all duration-500 hover:-translate-y-1`}>
            <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-primary text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>edit_note</span>
            </div>
            <h3 className="font-serif text-2xl mb-3 text-on-surface font-semibold">Trilhas e Caderno Espiritual</h3>
            <p className="text-on-surface-variant font-light leading-relaxed opacity-90 text-sm mb-4">
              Siga trilhas temáticas de formação e registre suas reflexões num caderno espiritual pessoal, com painel de progresso semanal.
            </p>
            <ul className="space-y-1 text-xs text-on-surface-variant/70">
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Trilhas por tema e nível</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Anotações com busca semântica</li>
              <li className="flex items-center gap-2"><span className="material-symbols-outlined text-primary text-sm">check_small</span>Painel de progresso e exportação</li>
            </ul>
          </div>
        </div>

        {/* Como funciona — demonstrações baseadas no produto real */}
        <div id="como-funciona" className="max-w-6xl w-full pt-8 pb-24 px-6 scroll-mt-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-on-surface mb-4">Como funciona</h2>
            <p className="text-on-surface-variant font-light leading-relaxed">
              Um retrato real das telas que você vai usar todos os dias.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Leitura Assistida */}
            <div className="bg-surface-container-low border border-outline-variant/20 rounded-2xl p-6">
              <h3 className="font-serif text-lg font-semibold text-on-surface mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">auto_stories</span>
                Leitura Assistida
              </h3>
              <div className="bg-surface-container-lowest/60 border border-outline-variant/10 rounded-xl p-4 mb-3">
                <p className="text-[10px] uppercase tracking-wide text-on-surface-variant/60 mb-1">Evangelho de João em 30 dias · Dia 12 de 30</p>
                <p className="font-serif text-base text-on-surface mb-2">"Eu sou a luz do mundo" (Jo 8,12)</p>
                <div className="w-full h-1.5 bg-surface-container-high rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: '40%' }} />
                </div>
                <p className="text-[10px] text-on-surface-variant/50 mt-1">40% concluído</p>
              </div>
              <p className="text-xs text-on-surface-variant font-light leading-relaxed">
                Planos de leitura guiados dia a dia, com "Aprofundar com IA" sempre que quiser entender melhor o texto.
              </p>
            </div>

            {/* Personas + Sínodo Virtual */}
            <div className="bg-surface-container-low border border-outline-variant/20 rounded-2xl p-6">
              <h3 className="font-serif text-lg font-semibold text-on-surface mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">forum</span>
                Personas de IA
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {['Padre Amigo', 'Teólogo', 'Catequista', 'Diretor Espiritual'].map((p) => (
                  <span key={p} className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">{p}</span>
                ))}
              </div>
              <p className="text-xs text-on-surface-variant font-light leading-relaxed mb-2">
                Cada persona responde com um tom e profundidade diferentes — da acolhida pastoral à precisão teológica.
              </p>
              <p className="text-xs text-on-surface-variant font-light leading-relaxed">
                <span className="text-primary font-medium">🕊️ Sínodo Virtual:</span> ative o modo em que duas personas respondem à mesma pergunta e recebem uma síntese final.
              </p>
            </div>

            {/* Caderno Espiritual */}
            <div className="bg-surface-container-low border border-outline-variant/20 rounded-2xl p-6">
              <h3 className="font-serif text-lg font-semibold text-on-surface mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">edit_note</span>
                Caderno Espiritual
              </h3>
              <div className="bg-surface-container-lowest/60 border border-outline-variant/10 rounded-xl p-4 mb-3">
                <p className="text-xs font-medium text-on-surface mb-1">Reflexão sobre as Bem-aventuranças</p>
                <p className="text-[11px] text-on-surface-variant/70 font-light leading-relaxed">"Bem-aventurados os pobres de espírito..." (Mt 5,3) — o que isso muda no meu dia a dia?</p>
              </div>
              <p className="text-xs text-on-surface-variant font-light leading-relaxed">
                Registre suas anotações e reencontre-as depois com busca semântica, não só por palavra exata.
              </p>
            </div>

            {/* Trilhas */}
            <div className="bg-surface-container-low border border-outline-variant/20 rounded-2xl p-6">
              <h3 className="font-serif text-lg font-semibold text-on-surface mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">map</span>
                Trilhas de Estudo
              </h3>
              <div className="bg-surface-container-lowest/60 border border-outline-variant/10 rounded-xl p-4 mb-3">
                <p className="text-xs font-medium text-on-surface mb-1">Fundamentos da Fé</p>
                <p className="text-[10px] text-on-surface-variant/60 mb-2">Etapa 5 de 12</p>
                <div className="w-full h-1.5 bg-surface-container-high rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: '42%' }} />
                </div>
              </div>
              <p className="text-xs text-on-surface-variant font-light leading-relaxed">
                Siga trilhas temáticas por nível e acompanhe seu progresso num painel dedicado, com histórico de atividade.
              </p>
            </div>
          </div>
        </div>

        {/* Fontes e limites da IA */}
        <div id="fontes" className="max-w-4xl w-full pb-24 px-6 scroll-mt-24">
          <div className="bg-surface-container-low border border-outline-variant/20 rounded-2xl p-6 md:p-8">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-on-surface mb-4 text-center">
              De onde vem o conteúdo — e onde estão os limites
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm text-on-surface-variant">
              {[
                'Bíblia Sagrada — os 73 livros do Cânon Católico',
                'Catecismo da Igreja Católica',
                'Documentos do Magistério e encíclicas papais',
                'Padres e Doutores da Igreja',
              ].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-sm">check_small</span>
                  <span className="font-light">{s}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-outline-variant/20 pt-5 space-y-2 text-xs text-on-surface-variant/80 font-light leading-relaxed">
              <p>
                O CatholicGPT é um projeto independente de formação católica com inteligência artificial — não possui vínculo
                institucional oficial com a Igreja Católica, dioceses ou paróquias.
              </p>
              <p>
                Como qualquer sistema de IA, o CatholicGPT pode cometer erros. Ele não substitui a confissão, a direção espiritual
                nem o aconselhamento pastoral de um sacerdote — para questões de consciência e discernimento, procure sempre um
                padre. Sempre que uma passagem ou ensinamento for essencial, verifique na fonte original.
              </p>
            </div>
          </div>
        </div>

        {/* Planos — carregados do catálogo do backend (P23A/P23C) */}
        <LandingPlansPreview navigate={navigate} isLight={isLight} />

        {/* Segurança e privacidade */}
        <div id="seguranca" className="max-w-4xl w-full pb-24 px-6 scroll-mt-24">
          <div className="bg-surface-container-low border border-outline-variant/20 rounded-2xl p-6 md:p-8">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-on-surface mb-4 text-center">
              Privacidade e controle dos seus dados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-on-surface-variant">
              <div className="flex items-start gap-2.5">
                <span className="material-symbols-outlined text-primary text-base shrink-0 mt-0.5">lock</span>
                <span className="font-light leading-relaxed">Suas conversas são processadas por IA sob conexão segura — nunca compartilhe CPF, cartão ou senhas no chat.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="material-symbols-outlined text-primary text-base shrink-0 mt-0.5">visibility_off</span>
                <span className="font-light leading-relaxed">O conteúdo religioso das suas conversas não é usado para anúncios nem vendido a terceiros.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="material-symbols-outlined text-primary text-base shrink-0 mt-0.5">download</span>
                <span className="font-light leading-relaxed">Suas anotações do Caderno Espiritual são exportáveis quando permitido pelo seu plano.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="material-symbols-outlined text-primary text-base shrink-0 mt-0.5">payments</span>
                <span className="font-light leading-relaxed">Pagamentos são processados pelo Stripe (padrão PCI-DSS) — nunca guardamos dados do seu cartão.</span>
              </div>
            </div>
            <div className="flex justify-center gap-6 mt-6 text-xs">
              <Link to="/privacidade" className="text-primary hover:underline font-medium">Política de Privacidade</Link>
              <Link to="/termos" className="text-primary hover:underline font-medium">Termos de Uso</Link>
            </div>
          </div>
        </div>

        {/* FAQ geral do produto */}
        <div id="faq" className="max-w-4xl w-full pb-24 px-6 scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-on-surface mb-8 text-center">Perguntas Frequentes</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {FAQS.map((faq, idx) => (
              <div key={idx} className="bg-surface-container-low/50 p-6 rounded-xl border border-outline-variant/10 space-y-2">
                <h4 className="font-serif font-bold text-sm text-on-surface flex items-start gap-2">
                  <span className="text-primary text-base font-bold shrink-0">?</span>
                  <span>{faq.question}</span>
                </h4>
                <p className="text-xs text-on-surface-variant/80 font-light leading-relaxed pl-3">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA final */}
        <div className="w-full pb-24 px-6">
          <div className="max-w-3xl mx-auto text-center bg-primary/5 border border-primary/20 rounded-2xl p-10 md:p-14">
            <h2 className="font-serif text-2xl md:text-4xl font-bold text-on-surface mb-4">
              Comece sua jornada de fé hoje mesmo
            </h2>
            <p className="text-on-surface-variant font-light leading-relaxed mb-8 max-w-xl mx-auto">
              Crie sua conta gratuita e experimente a Leitura Assistida, as personas de IA e o Caderno Espiritual sem compromisso.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="bg-primary text-on-primary px-10 py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all shadow-lg"
            >
              Começar gratuitamente
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full py-16 flex flex-col items-center justify-center text-center bg-surface-container-low">
        <div className="mb-8 flex flex-col items-center space-y-4">
          <img alt="Logo" className="w-12 h-12 rounded-full bg-white p-1 transition-all duration-500" src="/logo-192.png" />
          <p className="font-serif italic text-xl text-primary-container max-w-lg">
            "Buscai e achareis." — Mateus 7:7
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-on-surface-variant/50 text-sm mb-8">
          <Link to="/planos" className="hover:text-primary transition-colors">Planos</Link>
          <Link to="/termos" className="hover:text-primary transition-colors">Termos</Link>
          <Link to="/privacidade" className="hover:text-primary transition-colors">Privacidade</Link>
          <button className="hover:text-primary transition-colors" onClick={() => setModal('contact')}>Contato</button>
        </div>

        <div className="text-[10px] tracking-[0.2em] text-on-surface-variant/30 uppercase font-sans">
          © 2026 CatholicGPT — Ad Maiorem Dei Gloriam
        </div>
      </footer>

      {modal === 'contact' && <ContactModal onClose={() => setModal(null)} />}
    </div>
  );
};

export default LandingPage;